import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { AnsweringStep } from '@types'
import { LoadingSpinner, ThreeDotsLoader } from '@ui/components'
import { useEffect } from 'react'
import Skeleton from 'react-loading-skeleton'

import { useImmer } from 'use-immer'

import { useChatContext } from '../ChatProvider'

interface BotThinkingProps {
	readonly type: 'sentences' | 'dots' | 'skeletton';
	readonly answeringStep?: AnsweringStep | undefined;
}

export default function BotThinking({ type, answeringStep }: BotThinkingProps) {
	const [answeringSteps, updateAnsweringSteps] = useImmer<AnsweringStep[]>([])

	const { displaySources } = useChatContext()

	useEffect(() => {
		if (answeringStep) {
			updateAnsweringSteps(draft => {
				const index = draft.findIndex(step => step.step === answeringStep.step)

				if (index === -1) {
					draft.push(answeringStep)
				} else {
					draft[index] = answeringStep
				}
			})
		}
	}, [answeringStep])

	const analyzingStep = answeringSteps.find(step => step.step === 'analyzing')
	const retrievingStep = answeringSteps.find(step => step.step === 'retrieving')
	const answerStep = answeringSteps.find(step => step.step === 'answering')

	return (
		<>
			{ type === 'dots' && <ThreeDotsLoader /> }
			{
				type === 'sentences' && (
					<div className='thinking_sentence-container'>
						<p className='thinking_sentence thinking_sentence-1'>Scanning knowledge...</p>
						<p className='thinking_sentence thinking_sentence-2'>Connecting the dots...</p>
						<p className='thinking_sentence thinking_sentence-3'>Thinking...</p>
					</div>
				)
			}
			{
				type === 'skeletton' && (
					<div className='bg-gray-50 p-6 rounded-lg w-full'>
						<div className='text-sm italic text-gray-400 flex gap-1 items-center pb-2'>
							{
								analyzingStep?.status === 'completed'
									? (
										<>
											<FontAwesomeIcon icon='check-circle' className='w-4 h-4' />
											Analysis completed
										</>
									)
									: (
										<>
											<LoadingSpinner size='small' style='gray' />
											Analyzing your question...
										</>
									)
							}
						</div>
						<div className='w-full'>
							<Skeleton className='hiden show-1' count={ 3 } />
							<Skeleton className='w-3/4 hiden show-2' count={ 1 } />
							<Skeleton className='hiden show-3' count={ 1 } />
							{
								answerStep?.status === 'pending' && (
									<div className='text-sm italic text-gray-400 mt-2'>
										<p className='thinking_sentence thinking_sentence-1 mb-1'>Analysing documents...</p>
										<p className='thinking_sentence thinking_sentence-2'>Preparing the answer...</p>
									</div>
								)
							}
						</div>
						{
							retrievingStep?.step === 'retrieving' && displaySources && (
								<div className='flex items-center text-sm italic text-gray-400 mt-2'>
									<FontAwesomeIcon icon='bookmark' className='mr-2 ml-2 w-4 h-4' />
									<p>
										{
											retrievingStep.status === 'pending' && (
												<>
														Searching documents...
												</>
											)
										}
										{
											retrievingStep.status === 'completed' && (
												<>
													<span className='font-bold'>{ retrievingStep.data.amountOfResults } </span>
														document{ retrievingStep.data.amountOfResults > 1 ? 's' : '' } found to analyse
												</>
											)
										}
									</p>
								</div>
							)
						}
					</div>
				)
			}
		</>
	)
}
